import { Box, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useProjectsState } from '../hooks/useProjectsState';
import {
  getEnhanceOperation,
  getExecuteOperation,
  getGenerateOperation,
  type TestExecutionActionKey,
} from '../utils/projectExecutionLifecycle';
import { ExecutionCenter } from './ExecutionCenter';
import type { EnhancementTestPlanSelection } from '../interfaces/ui';

export const ExecutionCenterRoute = ({
  isToolAdmin,
}: {
  isToolAdmin: boolean;
}) => {
  const { projectId, iqeProjectId, runId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const {
    getProjectById,
    performProjectOperation,
    loadingIqeProjects,
    isPerformingAction,
    refreshProjectStatus,
    isStoppingRun,
    stopRun,
  } = useProjectsState(projectId!);

  const project = getProjectById(iqeProjectId!);

  if (!project) {
    if (loadingIqeProjects) {
      return (
        <Box style={{ padding: '16px', minHeight: '50vh' }}>
          <Skeleton
            variant="rect"
            height={120}
            style={{ marginBottom: '16px', borderRadius: '8px' }}
            animation="wave"
          />
          <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <Skeleton
              variant="rect"
              width={100}
              height={32}
              style={{ borderRadius: '4px' }}
              animation="wave"
            />
            <Skeleton
              variant="rect"
              width={100}
              height={32}
              style={{ borderRadius: '4px' }}
              animation="wave"
            />
            <Skeleton
              variant="rect"
              width={100}
              height={32}
              style={{ borderRadius: '4px' }}
              animation="wave"
            />
            <Skeleton
              variant="rect"
              width={100}
              height={32}
              style={{ borderRadius: '4px' }}
              animation="wave"
            />
          </Box>
          <Box>
            {[0, 1, 2, 3].map(i => (
              <Skeleton
                key={i}
                variant="rect"
                height={48}
                style={{ marginBottom: '8px', borderRadius: '4px' }}
                animation="wave"
              />
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography variant="body2" color="textSecondary">
          Project not found.
        </Typography>
      </Box>
    );
  }

  const testPlanName =
    'planName' in project.writeTestPlan
      ? project.writeTestPlan.planName
      : 'projectName' in project.writeTestPlan
      ? project.writeTestPlan.projectName
      : '';

  const handleViewDetailedResults = () => {
    navigate(`../${iqeProjectId}`, {
      state: {
        openResultsProjectId: iqeProjectId,
        projectsViewMode: (location.state as any)?.projectsViewMode,
      },
    });
  };

  const handleRefreshStatus = async () => {
    await refreshProjectStatus(projectId!, iqeProjectId!);
  };

  const handleStopRun = async () => {
    if (!isToolAdmin || !runId) return false;
    return stopRun(projectId!, iqeProjectId!, runId);
  };

  const handleExecuteSuites = async (
    action: TestExecutionActionKey,
    userPrompt = '',
    enhancementSelection?: EnhancementTestPlanSelection,
  ): Promise<string | null> => {
    if (!isToolAdmin) return null;

    const isExecuteOnlyMode =
      action === 'RUN' ||
      (action !== 'GENERATE' &&
        action !== 'ENHANCE' &&
        searchParams.get('mode') === 'execute');

    const isEnhanceMode =
      action === 'ENHANCE' ||
      (action !== 'GENERATE' &&
        action !== 'RUN' &&
        searchParams.get('mode') === 'enhance');

    const operation = isExecuteOnlyMode
      ? getExecuteOperation(project.status, project.runs?.length || 0)
      : isEnhanceMode
      ? getEnhanceOperation(project.status, project.runs?.length || 0)
      : getGenerateOperation(project.status, project.runs?.length || 0);

    const newRunId = await performProjectOperation(
      projectId!,
      iqeProjectId!,
      operation,
      userPrompt,
      enhancementSelection,
    );
    if (newRunId) {
      // Replace only the runId segment in the current path
      const newPath = location.pathname.replace(runId!, newRunId);
      const nextMode = isExecuteOnlyMode
        ? 'execute'
        : isEnhanceMode
        ? 'enhance'
        : 'generate';
      const nextTab = 'activity';
      navigate(`${newPath}?tab=${nextTab}&mode=${nextMode}`, { replace: true });

      return newRunId;
    }

    return null;
  };

  const testSuiteName =
    project.writeTestPlan.system === 'ado'
      ? project.writeTestPlan.suiteName
      : project.writeTestPlan.subFolderName;
  return (
    <ExecutionCenter
      project={project}
      projectName={project.name}
      appUrl={project.appUrl || ''}
      projectId={iqeProjectId!}
      apmNumber={projectId!}
      runId={runId}
      projectStatus={project.status}
      environment={project.environment || ''}
      testPlanName={testPlanName}
      testSuiteName={testSuiteName}
      frontendRepositoryName={project.frontendRepositoryName || ''}
      frontendRepositoryUrl={project.frontendRepositoryUrl || ''}
      frontendRepositoryBranch={project.frontendRepositoryBranch || ''}
      onViewDetailedResults={handleViewDetailedResults}
      onRefreshStatus={handleRefreshStatus}
      onStartExecution={handleExecuteSuites}
      onStopRun={handleStopRun}
      isToolAdmin={isToolAdmin}
      isPerformingAction={isPerformingAction}
      isStoppingRun={isStoppingRun}
    />
  );
};
